import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { AppointmentsService } from '../api/AppointmentsAPI';
import { StaffService } from '../api/StaffAPI';
import { useLightMode, usePagination, useTaxMode, shownQuote } from '../utils';
import { Header } from '../components/Header/Header';
import { DateRangeFilter } from '../components/DateRangeFilter/DateRangeFilter';
import { Pagination } from '../components/Pagination/Pagination';
import { AppointmentDrawer } from '../components/AppointmentDrawer/AppointmentDrawer';
import { SetPaymentMethodModal } from '../components/SetPaymentMethodModal/SetPaymentMethodModal';
import s from '../styles/staff.module.css';
import d from '../styles/dashboard.module.css';

const money = (cents) => (cents == null ? '—' : `$${(cents / 100).toLocaleString('en-US', {
  minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

const STATUS = {
  paid:       { label: 'Paid',        tone: 'pillPaid' },
  receivable: { label: 'Receivable',  tone: 'pillReceivable' },
  due:        { label: 'Payment due', tone: 'pillDue' },
  no_charge:  { label: 'No charge',   tone: 'pillNoCharge' },
};

const FILTERS = [
  { id: 'all',       label: 'All',            test: () => true },
  { id: 'attention', label: 'Needs attention', test: (r) => r.needsAttention },
  { id: 'paid',      label: 'Paid',           test: (r) => r.status === 'paid' },
  { id: 'receivable',label: 'Receivable',     test: (r) => r.status === 'receivable' },
  { id: 'due',       label: 'Payment due',    test: (r) => r.status === 'due' },
  { id: 'no_charge', label: 'No charge',      test: (r) => r.status === 'no_charge' },
];

function Tile({ label, value, note, tone }) {
  return (
    <div className={s.ledgerTile}>
      <div className={s.ledgerTileLabel}>{label}</div>
      <div className={`${s.ledgerTileValue} ${tone ? s[tone] : ''}`}>{value}</div>
      {note && <div className={s.ledgerTileNote}>{note}</div>}
    </div>
  );
}

const dt = (v) => (v ? new Date(v).toLocaleString('en-US', {
  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '—');

export function AppointmentsPage() {
  const [lightMode, toggleLightMode] = useLightMode();
  const [taxMode, setTaxMode] = useTaxMode();

  // Actual revenue is what the customer paid, so it never moves with the
  // switch — a payment already has tax inside it.
  //
  // A receivable is the awkward case: `actualCents` holds the invoice's pre-tax
  // value there, not a payment, because nothing has been collected yet. Showing
  // it in this column made an unpaid invoice look part-paid, and in after-tax
  // mode it read as a shortfall against the quote that does not exist. What is
  // owed on it belongs in Quoted and in the Receivables tile instead.
  const shownActual = useCallback(
    (r) => (r.status === 'receivable' ? null : r.actualCents),
    [],
  );

  // What an unpaid invoice is worth, which is a quote and so does follow the
  // switch. Only the Receivables tile uses this.
  const invoiceOwed = useCallback(
    (r) => shownQuote(r.actualCents, r.taxCents, taxMode),
    [taxMode],
  );
  const [range, setRange] = useState({ start: '', end: '' });
  const [filter, setFilter] = useState('all');
  const onRangeChange = useCallback((r) => setRange(r), []);

  const { data, isLoading } = useQuery({
    queryKey: ['appointment-ledger', range.start, range.end],
    queryFn: () => AppointmentsService.getLedger(range.start, range.end),
    enabled: !!range.start && !!range.end,
    placeholderData: (prev) => prev,
  });
  const rows = useMemo(() => data?.appointments || [], [data]);

  // Totals describe the whole range, not the current filter — a filtered view
  // should not make the day's money look smaller than it is.
  const totals = useMemo(() => rows.reduce((t, r) => {
    if (r.status === 'paid')            t.collected += r.actualCents || 0;
    // A receivable records the pre-tax invoice value, so it follows the switch.
    else if (r.status === 'receivable') t.receivable += invoiceOwed(r) || 0;
    // Nothing has been collected on these, so the figure is the quote.
    else if (r.status === 'due')      { t.due += shownQuote(r.quotedCents, r.taxCents, taxMode) || 0; t.dueCars += 1; }
    if (r.status === 'no_charge')       t.noChargeCars += 1;
    t.tips += r.tipCents || 0;
    return t;
  }, { collected: 0, receivable: 0, due: 0, dueCars: 0, noChargeCars: 0, tips: 0 }), [rows, taxMode, invoiceOwed]);

  const visible = useMemo(
    () => rows.filter((FILTERS.find(f => f.id === filter) || FILTERS[0]).test),
    [rows, filter]);

  const { page, pageCount, pageItems, setPage } = usePagination(visible, 25);

  // The drawer resolves crew ids to names, exactly as the queue does.
  const { data: staffData } = useQuery({
    queryKey: ['staff-roster'],
    queryFn: () => StaffService.list('active'),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  const staff = staffData?.staffs || [];

  const queryClient = useQueryClient();
  const [detailAppt, setDetailAppt] = useState(null);
  const [payAppt, setPayAppt] = useState(null);
  // Opened from the drawer, so closing the payment panel returns there.
  const [payReturnTo, setPayReturnTo] = useState(null);

  const refreshRow = (appt) => {
    queryClient.invalidateQueries({ queryKey: ['appointment-payment', appt.externalId || appt.id] });
    queryClient.invalidateQueries({ queryKey: ['appointment-ledger', range.start, range.end] });
    queryClient.invalidateQueries({ queryKey: ['attention'] });
  };

  return (
    <div className={lightMode ? `${s.page} ${s.lightPage}` : s.page}>
      <Header lightMode={lightMode} toggleLightMode={toggleLightMode}
        taxMode={taxMode} onTaxModeChange={setTaxMode} />

      <div className={s.body} style={{ gridTemplateColumns: '1fr' }}>
        <div>
          <div className={s.tableHeaderRow}>
            <div className={s.sectionLabel} style={{ marginBottom: 0 }}>
              Appointments
              <span className={s.ledgerIntro}>Every job, and how its money was handled</span>
            </div>
          </div>

          {/* Filters on their own line: what to show on the left, when on the right. */}
          <div className={s.filterRow}>
            <div className={s.chipRow}>
                {FILTERS.map(f => (
                  <button key={f.id} onClick={() => { setFilter(f.id); setPage(1); }}
                          className={`${s.chip} ${filter === f.id ? s.chipActive : ''}`}>
                    {f.label}
                  </button>
                ))}
            </div>
            <DateRangeFilter onChange={onRangeChange} />
          </div>

          <div className={s.ledgerTiles}>
            <Tile label="Collected"   value={money(totals.collected)} tone="ledgerGreen"
                  note="service revenue, excludes tax and tip" />
            <Tile label="Receivables" value={money(totals.receivable)} note="invoice + hotel" />
            <Tile label="Payment due" value={money(totals.due)} tone="ledgerRed"
                  note={`${totals.dueCars} car${totals.dueCars === 1 ? '' : 's'} unpaid`} />
            <Tile label="No charge"   value={String(totals.noChargeCars)}
                  note="comped or redeemed" />
            <Tile label="Tips earned" value={money(totals.tips)} tone="ledgerGreen"
                  note="locked to crew at finish" />
          </div>

          <div className={s.tableWrap}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Date / Time</th><th>Customer</th><th>Location</th><th>Services</th>
                  <th className={s.num}>{taxMode === 'after' ? 'Quoted incl. tax' : 'Quoted rev.'}</th>
                  <th className={s.num}>{taxMode === 'after' ? 'Actual incl. tax' : 'Actual rev.'}</th>
                  <th className={s.num}>Tax</th>
                  <th className={s.num}>Tip</th>
                  <th>Status</th><th>Method</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && <tr><td colSpan={10}>Loading…</td></tr>}
                {!isLoading && !pageItems.length && (
                  <tr><td colSpan={10}>No appointments in this range.</td></tr>
                )}
                {pageItems.map(r => {
                  const st = STATUS[r.status] || STATUS.due;
                  return (
                    <tr key={r.externalId} onClick={() => setDetailAppt(r)}
                        style={{ cursor: 'pointer' }}>
                      <td>{dt(r.startAt)}</td>
                      <td><strong>{r.customer || '—'}</strong></td>
                      <td>{r.locationName}</td>
                      <td>
                        {r.services.length ? r.services.map((x, i) => <div key={i}>{x}</div>) : '—'}
                      </td>
                      <td className={s.num}>{money(shownQuote(r.quotedCents, r.taxCents, taxMode))}</td>
                      <td className={s.num}>{money(shownActual(r))}</td>
                      <td className={s.num}>{money(r.taxCents)}</td>
                      <td className={s.num}>{money(r.tipCents)}</td>
                      <td>
                        <span className={`${s.statusPill} ${s[st.tone]}`}>{st.label}</span>
                        {r.needsVerification && (
                          <span className={s.subCell}>awaiting verification</span>
                        )}
                      </td>
                      <td>
                        {r.methodTitle || 'Not set'}
                        {r.billedTo && <span className={s.subCell}>{r.billedTo}</span>}
                        {r.invoiceNumber && <span className={s.subCell}>{r.invoiceNumber}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className={s.tableHeaderRow} style={{ marginTop: 10 }}>
            <div className={s.ledgerIntro} style={{ marginLeft: 0 }}>
              {visible.length} of {rows.length} appointments in range
            </div>
            <Pagination page={page} pageCount={pageCount} onPage={setPage} />
          </div>
        </div>
      </div>

      {/* The drawer and payment panel take their light theme from a `.lightRoot`
          ancestor in dashboard.module.css — this page's own wrapper uses
          staff.module.css, so without this they would always render dark.
          `display: contents` keeps the element in the tree for the selector to
          match while giving it no box of its own. */}
      <div className={lightMode ? d.lightRoot : undefined} style={{ display: 'contents' }}>
      {detailAppt && (
        <AppointmentDrawer
          appt={detailAppt}
          staff={staff}
          onClose={() => setDetailAppt(null)}
          onVerified={() => refreshRow(detailAppt)}
          onTakePayment={(a) => { setDetailAppt(null); setPayReturnTo(a); setPayAppt(a); }}
          onChangeMethod={(a) => { setDetailAppt(null); setPayReturnTo(a); setPayAppt(a); }}
        />
      )}

      {payAppt && (
        <SetPaymentMethodModal
          appt={payAppt}
          onClose={() => {
            setPayAppt(null);
            if (payReturnTo) setDetailAppt(payReturnTo);
            setPayReturnTo(null);
          }}
          onSaved={() => refreshRow(payAppt)}
        />
      )}
      </div>
    </div>
  );
}
